/**
 * 输入验证工具类
 * 用于在前端验证用户输入，提前拦截非法字符和SQL注入
 */

// 非法关键字列表
const ILLEGAL_KEYWORDS = [
  'admin', 'root', 'system', 'administrator', 'test', 'guest',
  'login', 'logout', 'register', 'signup', 'signin', 'signout',
  'delete', 'drop', 'insert', 'update', 'select', 'create',
  'alter', 'truncate', 'grant', 'revoke', 'union', 'exec'
];

// SQL注入关键字
const SQL_INJECTION_KEYWORDS = [
  "'", '"', ';', '--', '/*', '*/', 'xp_', 'sp_',
  '1=1', '1=0', 'waitfor', 'delay', 'benchmark',
  'or ', 'and ', 'xor ', 'not ', 'union', 'select', 'insert',
  'delete', 'update', 'drop', 'create', 'alter', 'exec',
  'execute', 'script', 'javascript', 'alert', 'onerror', 'onload'
];

// XSS攻击字符
const XSS_KEYWORDS = [
  '<script', '</script>', 'javascript:', 'onerror=', 'onload=',
  'onclick=', 'onmouseover=', 'onfocus=', 'onblur=', 'eval(',
  'expression(', 'vbscript:', 'onreadystatechange=', 'ondblclick='
];

/**
 * 验证用户名
 * @param {string} username - 用户名
 * @returns {object} { valid: boolean, message: string }
 */
export const validateUsername = (username) => {
  if (!username || username.trim() === '') {
    return { valid: false, message: '用户名不能为空' };
  }

  const trimmed = username.trim();

  // 检查长度
  if (trimmed.length < 3 || trimmed.length > 20) {
    return { valid: false, message: '用户名长度必须在3-20个字符之间' };
  }

  // 检查格式（只允许字母、数字、下划线）
  if (!/^[a-zA-Z0-9_]+$/.test(trimmed)) {
    return { valid: false, message: '用户名只能包含字母、数字和下划线' };
  }

  // 检查非法关键字
  const lower = trimmed.toLowerCase();
  for (const keyword of ILLEGAL_KEYWORDS) {
    if (lower.includes(keyword)) {
      return { valid: false, message: '用户名包含非法关键字' };
    }
  }

  // 检查SQL注入
  for (const keyword of SQL_INJECTION_KEYWORDS) {
    if (lower.includes(keyword)) {
      return { valid: false, message: '用户名包含非法字符' };
    }
  }

  return { valid: true };
};

/**
 * 验证邮箱
 * @param {string} email - 邮箱地址
 * @returns {object} { valid: boolean, message: string }
 */
export const validateEmail = (email) => {
  if (!email || email.trim() === '') {
    return { valid: false, message: '邮箱不能为空' };
  }

  const trimmed = email.trim().toLowerCase();

  // 检查格式
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  if (!emailRegex.test(trimmed)) {
    return { valid: false, message: '邮箱格式不正确' };
  }

  // 检查SQL注入
  for (const keyword of SQL_INJECTION_KEYWORDS) {
    if (trimmed.includes(keyword)) {
      return { valid: false, message: '邮箱包含非法字符' };
    }
  }

  return { valid: true };
};

/**
 * 验证密码
 * @param {string} password - 密码
 * @returns {object} { valid: boolean, message: string }
 */
export const validatePassword = (password) => {
  if (!password || password === '') {
    return { valid: false, message: '密码不能为空' };
  }

  // 检查长度
  if (password.length < 8) {
    return { valid: false, message: '密码长度至少8位' };
  }

  // 检查格式（至少包含字母和数字）
  if (!/^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d@$!%*#?&]{8,}$/.test(password)) {
    return { valid: false, message: '密码必须包含字母和数字' };
  }

  // 检查SQL注入
  for (const keyword of SQL_INJECTION_KEYWORDS) {
    if (password.toLowerCase().includes(keyword)) {
      return { valid: false, message: '密码包含非法字符' };
    }
  }

  return { valid: true };
};

/**
 * 验证昵称
 * @param {string} nickname - 昵称
 * @returns {object} { valid: boolean, message: string }
 */
export const validateNickname = (nickname) => {
  if (!nickname || nickname.trim() === '') {
    return { valid: false, message: '昵称不能为空' };
  }

  const trimmed = nickname.trim();

  // 检查长度
  if (trimmed.length < 2 || trimmed.length > 20) {
    return { valid: false, message: '昵称长度必须在2-20个字符之间' };
  }

  // 检查格式（允许中文、字母、数字、常用符号）
  if (!/^[\u4e00-\u9fa5a-zA-Z0-9_\-.\s]{2,20}$/.test(trimmed)) {
    return { valid: false, message: '昵称包含非法字符' };
  }

  const lower = trimmed.toLowerCase();

  // 检查SQL注入
  for (const keyword of SQL_INJECTION_KEYWORDS) {
    if (lower.includes(keyword)) {
      return { valid: false, message: '昵称包含非法字符' };
    }
  }

  // 检查XSS
  for (const keyword of XSS_KEYWORDS) {
    if (lower.includes(keyword)) {
      return { valid: false, message: '昵称包含非法字符' };
    }
  }

  return { valid: true };
};

/**
 * 清理字符串（用于输出时的HTML转义）
 * @param {string} input - 输入字符串
 * @returns {string} 转义后的字符串
 */
export const sanitize = (input) => {
  if (!input) return '';

  return input
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;');
};

/**
 * 检查是否包含SQL注入
 * @param {string} input - 输入字符串
 * @returns {boolean}
 */
export const containsSqlInjection = (input) => {
  if (!input) return false;

  const lower = input.toLowerCase();
  for (const keyword of SQL_INJECTION_KEYWORDS) {
    if (lower.includes(keyword)) {
      return true;
    }
  }
  return false;
};

/**
 * 检查是否包含XSS攻击
 * @param {string} input - 输入字符串
 * @returns {boolean}
 */
export const containsXss = (input) => {
  if (!input) return false;

  const lower = input.toLowerCase();
  for (const keyword of XSS_KEYWORDS) {
    if (lower.includes(keyword)) {
      return true;
    }
  }
  return false;
};

/**
 * 验证通用文本字段（用于标题、名称等）
 * @param {string} input - 输入字符串
 * @param {object} options - 验证选项 {min, max, required, fieldName}
 * @returns {object} { valid: boolean, message: string }
 */
export const validateTextField = (input, options = {}) => {
  const {
    min = 1,
    max = 200,
    required = false,
    fieldName = '字段'
  } = options;

  const trimmed = input?.trim() || '';

  // 检查是否为空
  if (required && trimmed === '') {
    return { valid: false, message: `${fieldName}不能为空` };
  }

  // 如果非必填且为空，直接通过
  if (!required && trimmed === '') {
    return { valid: true };
  }

  // 检查长度
  if (trimmed.length < min || trimmed.length > max) {
    return { valid: false, message: `${fieldName}长度必须在${min}-${max}个字符之间` };
  }

  // 检查SQL注入
  if (containsSqlInjection(trimmed)) {
    return { valid: false, message: `${fieldName}包含非法字符` };
  }

  // 检查XSS
  if (containsXss(trimmed)) {
    return { valid: false, message: `${fieldName}包含非法字符` };
  }

  return { valid: true };
};

/**
 * 验证描述字段（允许更多字符）
 * @param {string} input - 输入字符串
 * @param {object} options - 验证选项 {min, max, required, fieldName}
 * @returns {object} { valid: boolean, message: string }
 */
export const validateDescription = (input, options = {}) => {
  const {
    min = 0,
    max = 1000,
    required = false,
    fieldName = '描述'
  } = options;

  const trimmed = input?.trim() || '';

  // 检查是否为空
  if (required && trimmed === '') {
    return { valid: false, message: `${fieldName}不能为空` };
  }

  // 如果非必填且为空，直接通过
  if (!required && trimmed === '') {
    return { valid: true };
  }

  // 检查长度
  if (trimmed.length < min || trimmed.length > max) {
    return { valid: false, message: `${fieldName}长度必须在${min}-${max}个字符之间` };
  }

  // 检查SQL注入
  if (containsSqlInjection(trimmed)) {
    return { valid: false, message: `${fieldName}包含非法字符` };
  }

  return { valid: true };
};

/**
 * 验证网站URL
 * @param {string} url - URL字符串
 * @returns {object} { valid: boolean, message: string }
 */
export const validateWebsite = (url) => {
  const trimmed = url?.trim() || '';

  // 如果为空，直接通过（非必填）
  if (trimmed === '') {
    return { valid: true };
  }

  // 检查SQL注入
  if (containsSqlInjection(trimmed)) {
    return { valid: false, message: '个人网站包含非法字符' };
  }

  // 检查URL格式
  const urlRegex = /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/;
  if (!urlRegex.test(trimmed)) {
    return { valid: false, message: '个人网站格式不正确' };
  }

  return { valid: true };
};
